/**
 * URL Anchor Parse supports reading and creating key/value pairs
 * from the anchor part of an URL (e.g. http://www.alexi.ch/foo/bar#key=val/key2=val1,val2).
 * It parses the part after the anchor hash ('#') into key/value pairs.
 *
 * This modules provides the following exports:
 *
 * * function parse(url)
 *     Takes an URL and returns a parameter object.
 *     Example:
 *     parse('http://www.alexi.ch/foo/bar#key=val/key2=val1,val2') => {key:'val',key2:['val1','val2']}
 *
 * * function makeValue(value)
 *     normalizes / encodes a value to be used as value string in the URL. `value` may be an array or a single value.
 *     Examples:
 *     makeValue('Hello, World!') => 'Hello%2C%20World!'
 *     makeValue(['un, dos','tres!']) => 'un%2C%20dos,tres!'
 *
 * * function makeParam(key, value)
 *     normalizes / encodes a key/value pair into a full parameter string to be used in the URL.
 *     `value` may be an array or a single value.
 *     Examples:
 *     makeParam('foo','bar room') => 'foo=bar%20room'
 *     makeParam('foo',['bar','baz']) => 'foo=bar,baz'
 *
 * * function makeUrl(params)
 *     creates a string from a key/value parameter object to be used as url part (anchor).
 *     Examples:
 *     makeUrl({foo: 'bar',baz: ['one','two']}) => foo=bar/baz=one,two
 *
 * * object separators
 *     Lets you define the separators used to separate parameters, values, and key:
 *     - separators.param: Separates whole key/value pairs from each other (default: '/')
 *     - separators.key: Separates the param key from the value(s) (default: '=')
 *     - separators.value: separates multiple (array) values from each other (default: ',')
 *
 * (c) 2017 Alexander Schenkel, Kaden und Partner AG
 */

let separators = {
    /** param separator: separates whole key/value pairs from each other */
    param: '/',

    /** key separator: separates the key from the values */
    key: '=',

    /** value separator: separates multiple (array) values from each other */
    value: ',',
};

/**
 * Takes an URL and returns a parameter object.
 * Example:
 * parse('http://www.alexi.ch/foo/bar#key=val/key2=val1,val2') => {key:'val',key2:['val1','val2']}
 *
 * @param string url The URL to be parsed. Everything before (and including) the '#' is ignored.
 * @return Object A key/value pair object containing the parsed parameters
 */
const parse = (url) => {
    let hash = url.replace(/^[^#]*#/, '');
    let params = hash.split(separators.param);
    let result = {};
    params.forEach((item) => {
        let split = item.split(separators.key);
        if (split.length === 2) {
            let key = split[0] || '__unknown__';
            let values = split[1].split(separators.value).map((item) => decodeURIComponent(String(item)));
            result[key] = values.length === 1 ? values[0] : values;
        }
    });
    return result;
};

/**
 * normalizes / encodes a value to be used as value string in the URL. `value` may be an array or a single value.
 * Examples:
 * makeValue('Hello, World!') => 'Hello%2C%20World!'
 * makeValue(['un, dos','tres!']) => 'un%2C%20dos,tres!'
 *
 * @param String/Array value The value to be normalized. Each item will be converted to a string.
 * @return String The normalized value
 */
const makeValue = (value) => {
    if (!(value instanceof Array)) {
        value = [value];
    }
    value = value.map((item) => encodeURIComponent(String(item)));
    return value.join(separators.value);
};

/**
 * normalizes / encodes a key/value pair into a full parameter string to be used in the URL.
 * `value` may be an array or a single value.
 * Examples:
 * makeParam('foo','bar room') => 'foo=bar%20room'
 * makeParam('foo',['bar','baz']) => 'foo=bar,baz'
 *
 * @param String key The key (name) of the parameter
 * @param String/Array value The value to be normalized. Each item will be converted to a string.
 * @return String The normalized key/value string
 */
const makeParam = (key, value) => {
    let val = makeValue(value);
    return `${key}${separators.key}${val}`;
};

/**
 * creates a string from a key/value parameter object to be used as url part (anchor).
 * Examples:
 * makeUrl({foo: 'bar',baz: ['one','two']}) => foo=bar/baz=one,two
 *
 * @param Object params The key/value object to be normalized
 * @return String The normalized key/value string of the whole params object.
 */
const makeUrl = (params) => {
    let keys = Object.keys(params);
    let strings = [];
    keys.forEach((key) => {
        strings.push(makeParam(key, params[key]));
    });

    return strings.join(separators.param);
};

export default {
    separators,
    parse,
    makeValue,
    makeParam,
    makeUrl,
};
